import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { PlusCircle, Eye, Globe } from 'lucide-react'

interface BlogPost {
  post_id: string
  slug: string
  kind: 'post' | 'case_study' | 'methodology'
  title: string
  status: 'draft' | 'published' | 'archived'
  industry?: string
  published_at?: number
}

const SEED_POSTS: BlogPost[] = [
  { post_id: 'seed_001', slug: 'agency-cut-research-time-by-60-percent', kind: 'case_study', title: 'How [CUSTOMER_NAME] Cut Research Time by 60%', status: 'draft', industry: 'saas' },
  { post_id: 'seed_002', slug: 'insurance-carrier-killed-naic-complaint-rate', kind: 'case_study', title: 'How [CARRIER_NAME] Reduced NAIC Complaint Rate', status: 'draft', industry: 'insurance' },
  { post_id: 'seed_003', slug: 'b2b-saas-reallocated-retention-spend', kind: 'case_study', title: 'How [COMPANY_NAME] Reallocated Retention Spend', status: 'draft', industry: 'saas' },
]

const KIND_LABEL: Record<string, string> = {
  post: 'Blog post',
  case_study: 'Case study',
  methodology: 'Methodology',
}

export function BlogAdminPage() {
  const [posts, setPosts] = useState<BlogPost[]>(SEED_POSTS)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [kindFilter, setKindFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const displayed = posts.filter(p =>
    (kindFilter === 'all' || p.kind === kindFilter) &&
    (statusFilter === 'all' || p.status === statusFilter)
  )

  function publish(id: string) {
    setPosts(prev => prev.map(p =>
      p.post_id === id ? { ...p, status: 'published', published_at: Date.now() } : p
    ))
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Blog Admin</h1>
          <p className="text-sm text-slate-500 mt-1">Manage blog posts, case studies, and methodology articles.</p>
        </div>
        <Button variant="primary" size="sm">
          <PlusCircle className="w-4 h-4 mr-1.5" />New post
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'post', 'case_study', 'methodology'].map(k => (
          <button
            key={k}
            className={cn('text-sm px-3 py-1.5 rounded-lg border transition-colors', kindFilter === k ? 'bg-echo-indigo text-white border-echo-indigo' : 'border-border-gray text-slate-500 hover:border-slate-300')}
            onClick={() => setKindFilter(k)}
          >
            {k === 'all' ? 'All kinds' : KIND_LABEL[k] ?? k}
          </button>
        ))}
        <div className="w-px bg-border-gray mx-1" />
        {['all', 'draft', 'published'].map(s => (
          <button
            key={s}
            className={cn('text-sm px-3 py-1.5 rounded-lg border transition-colors', statusFilter === s ? 'bg-slate-700 text-white border-slate-700' : 'border-border-gray text-slate-500 hover:border-slate-300')}
            onClick={() => setStatusFilter(s)}
          >
            {s === 'all' ? 'All statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Draft seed notice */}
      <Card className="bg-echo-indigo/5 border-echo-indigo/20">
        <p className="text-xs text-echo-indigo font-medium mb-0.5">3 case study drafts ready to fill in</p>
        <p className="text-xs text-slate-600">
          These drafts have [BRACKETED] placeholder tokens. Fill them in with real customer data and publish when customers approve naming.
          Files are in <code className="text-echo-indigo">src/data/blog-seed/</code>.
        </p>
      </Card>

      <div className="space-y-3">
        {displayed.map(post => (
          <Card key={post.post_id}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge variant={post.status === 'published' ? 'success' : 'default'} size="sm">
                    {post.status}
                  </Badge>
                  <span className="text-xs text-slate-400">{KIND_LABEL[post.kind]}</span>
                  {post.industry && <span className="text-xs text-slate-400 capitalize">{post.industry.replace('_', ' ')}</span>}
                </div>
                <p className="text-sm font-medium text-deep-ink">{post.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">/{post.slug}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="ghost" size="sm">
                  <Eye className="w-3.5 h-3.5 mr-1" />Edit
                </Button>
                {post.status === 'draft' && (
                  <Button variant="primary" size="sm" onClick={() => publish(post.post_id)}>
                    <Globe className="w-3.5 h-3.5 mr-1" />Publish
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}
